import { useQuery } from '@tanstack/react-query';
import { Badge, Card, Checkbox, EmptyState, ErrorState, LoadingState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, formatDateTime } from '@lawbee/ui';
import { ScrollText, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { api, qs } from '../../lib/api';
import { humanize } from '../../lib/hooks';
import { can, useMe } from '../../lib/me';
import type { AuditEntry } from '../../lib/types';

export function AuditSettingsPage() {
  const me = useMe();
  if (!can(me, 'audit')) {
    return (
      <Card>
        <EmptyState icon={<ShieldOff />} title="No access" description="Only owners and lawyers can view the audit log." />
      </Card>
    );
  }
  return <AuditLog />;
}

function actionLabel(action: string): string {
  return humanize(action.replace(/\./g, ' '));
}

function AuditLog() {
  const [clientOnly, setClientOnly] = useState(false);
  const q = useQuery({
    queryKey: ['audit', clientOnly],
    queryFn: () => api.get<{ entries: AuditEntry[] }>(`/api/workspace/audit${qs({ limit: 200, clientData: clientOnly ? 1 : undefined })}`),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-[19px] font-semibold tracking-tight">Audit log</h2>
          <p className="text-sm text-muted-foreground">Every sign-in, settings change and access to client data. Showing the latest 200 entries.</p>
        </div>
        <Checkbox label="Client data access only" checked={clientOnly} onCheckedChange={(v) => setClientOnly(v === true)} />
      </div>
      {q.isLoading ? <LoadingState label="Loading audit log" /> : null}
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && q.data.entries.length === 0 ? (
        <Card>
          <EmptyState icon={<ScrollText />} title="No entries" description={clientOnly ? 'No client data has been accessed yet.' : 'Activity will appear here.'} />
        </Card>
      ) : null}
      {q.data && q.data.entries.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Record</TableHead>
                <TableHead>
                  <span className="sr-only">Client data</span>
                </TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data.entries.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="figures whitespace-nowrap text-[13px]">{formatDateTime(e.createdAt)}</TableCell>
                  <TableCell className="max-w-52">
                    <span className="block truncate">{e.actorLabel ?? '—'}</span>
                    <span className="block text-xs text-muted-foreground">{humanize(e.actorType)}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{actionLabel(e.action)}</TableCell>
                  <TableCell className="max-w-56">
                    {e.entityType ? <span className="block">{humanize(e.entityType)}</span> : <span className="text-muted-foreground">—</span>}
                    {e.entityId ? <span className="figures block truncate text-xs text-muted-foreground">{e.entityId}</span> : null}
                  </TableCell>
                  <TableCell>{e.clientData ? <Badge variant="warning">Client data</Badge> : null}</TableCell>
                  <TableCell className="figures whitespace-nowrap text-xs text-muted-foreground">{e.ip ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}
